import React, { useState, useEffect, useCallback } from "react";
import {
	Box,
	Typography,
	Button,
	Grid,
	CircularProgress,
	Card,
	CardContent,
	CardActions,
	Divider,
	Chip,
} from "@mui/material";
import { useFirebase } from "../../context/FirebaseContext";
import AddIcon from "@mui/icons-material/Add";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { useSnackbar } from "notistack";

// Mapeo de IDs de guías a portadas disponibles
const GUIDE_COVER_MAP = {
	kioto: require("../../assets/guia-kioto.png"),
	tokio: require("../../assets/guia-tokio.png"),
	ny: require("../../assets/guia-ny.png"),
	londres: require("../../assets/guia-londres.png"),
	// Mapeo adicional para otras guías usando portadas genéricas
	japan: require("../../assets/portadas/japan-cover.png"),
	spain: require("../../assets/portadas/spain-cover.png"),
	italia: require("../../assets/portadas/italia-cover.png"),
	francia: require("../../assets/portadas/francia-cover.png"),
	grecia: require("../../assets/portadas/grecia-cover.png"),
	tailandia: require("../../assets/portadas/tailandia-cover.png"),
	corea: require("../../assets/portadas/corea-cover.png"),
	mexico: require("../../assets/portadas/mexico-cover.png"),
	peru: require("../../assets/portadas/peru-cover.png"),
	portugal: require("../../assets/portadas/portugal-cover.png"),
	alemania: require("../../assets/portadas/alemania-cover.png"),
	uk: require("../../assets/portadas/uk-cover.png"),
	travel: require("../../assets/portadas/travel-cover.png"),
};

const ExternalGuides = () => {
	const [prebuiltGuides, setPrebuiltGuides] = useState([]);
	const [loading, setLoading] = useState(false);
	const { enqueueSnackbar } = useSnackbar();

	const { getPrebuiltGuides, importPrebuiltGuide } = useFirebase();

	const loadPrebuiltGuides = useCallback(async () => {
		setLoading(true);
		try {
			const guides = await getPrebuiltGuides();
			setPrebuiltGuides(guides);
		} catch (error) {
			console.error("Error loading prebuilt guides:", error);
			enqueueSnackbar("Error al cargar las guías precargadas", {
				variant: "error",
			});
		} finally {
			setLoading(false);
		}
	}, [getPrebuiltGuides, enqueueSnackbar]);

	useEffect(() => {
		loadPrebuiltGuides();
	}, [loadPrebuiltGuides]);

	const handleImportPrebuiltGuide = async (guideId) => {
		try {
			const result = await importPrebuiltGuide(guideId);
			if (result) {
				enqueueSnackbar(
					`Guía "${result.project.name}" importada correctamente. Ahora puedes verla en "Tus proyectos".`,
					{ variant: "success" }
				);
				// Recargar la lista de proyectos si es necesario
				window.location.reload();
			}
		} catch (error) {
			console.error("Error importing prebuilt guide:", error);
			enqueueSnackbar("Error al importar la guía. Inténtalo de nuevo.", {
				variant: "error",
			});
		}
	};

	// Función para obtener la portada de una guía
	const getGuideCover = (guide) => {
		// Primero intentar con el ID de la guía
		if (GUIDE_COVER_MAP[guide.id]) {
			return GUIDE_COVER_MAP[guide.id];
		}
		// Si no existe, intentar con el coverImage del backend
		if (guide.coverImage) {
			return guide.coverImage;
		}
		// Si no hay portada, usar una genérica
		return GUIDE_COVER_MAP.travel || null;
	};

	return (
		<Box>
			{loading ? (
				<Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
					<CircularProgress size={32} sx={{ color: "rgb(47, 127, 224)" }} />
				</Box>
			) : (
				<Grid container spacing={3}>
					{prebuiltGuides.map((guide) => {
						const coverImage = getGuideCover(guide);
						return (
						<Grid item xs={12} sm={6} md={4} key={guide.id}>
							<Card
								sx={{
									height: "100%",
									display: "flex",
									flexDirection: "column",
										border: "2px solid rgba(47, 127, 224, 0.2)",
									borderRadius: 3,
										boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
										transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
										overflow: "hidden",
										position: "relative",
									"&:hover": {
											boxShadow: "0 16px 40px rgba(47, 127, 224,0.25)",
											transform: "translateY(-8px)",
											borderColor: "rgb(47, 127, 224)",
									},
								}}
							>
									{/* Imagen de portada */}
									{coverImage && (
										<Box
											sx={{
												width: "100%",
												height: 200,
												position: "relative",
												overflow: "hidden",
												backgroundColor: "rgba(47, 127, 224, 0.05)",
											}}
										>
											<img
												src={coverImage}
												alt={guide.name}
												style={{
													width: "100%",
													height: "100%",
													objectFit: "cover",
													transition: "transform 0.3s ease",
												}}
												onMouseEnter={(e) => {
													e.currentTarget.style.transform = "scale(1.05)";
												}}
												onMouseLeave={(e) => {
													e.currentTarget.style.transform = "scale(1)";
												}}
											/>
											{/* Overlay gradiente */}
											<Box
												sx={{
													position: "absolute",
													bottom: 0,
													left: 0,
													right: 0,
													height: "60%",
													background:
														"linear-gradient(to top, rgba(0,0,0,0.6), transparent)",
												}}
											/>
											{/* Badge de días si existe */}
											{guide.days && (
												<Chip
													label={`${guide.days} días`}
													size="small"
													sx={{
														position: "absolute",
														top: 12,
														right: 12,
														backgroundColor: "rgba(255,255,255,0.95)",
														color: "rgb(47, 127, 224)",
														fontWeight: 700,
														fontSize: "0.75rem",
														boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
													}}
												/>
											)}
										</Box>
									)}
									<CardContent
										sx={{
											flex: 1,
											display: "flex",
											flexDirection: "column",
											padding: 2.5,
											"&:last-child": { paddingBottom: 2.5 },
											}}
										>
										{/* Título y descripción */}
										<Box sx={{ flex: 1, mb: 2 }}>
											<Typography
												variant="h6"
												sx={{
													fontWeight: 700,
													color: "#1e293b",
													mb: 1,
													wordBreak: "break-word",
													fontSize: "1.25rem",
													lineHeight: 1.3,
												}}
											>
												{guide.name}
											</Typography>
											<Typography
												variant="body2"
												color="text.secondary"
												sx={{
													lineHeight: 1.6,
													fontSize: "0.9rem",
													mb: 2,
												}}
											>
												{guide.description}
											</Typography>
											{/* Tags si existen */}
											{guide.tags && guide.tags.length > 0 && (
												<Box
													sx={{
														display: "flex",
														flexWrap: "wrap",
														gap: 0.5,
														mt: "auto",
													}}
												>
													{guide.tags.slice(0, 3).map((tag, idx) => (
														<Chip
															key={idx}
															label={tag}
															size="small"
															sx={{
																backgroundColor: "rgba(47, 127, 224, 0.1)",
																color: "rgb(47, 127, 224)",
																fontSize: "0.7rem",
																height: 22,
																fontWeight: 600,
															}}
														/>
													))}
										</Box>
											)}
									</Box>
								</CardContent>
								<Divider />
									<CardActions sx={{ padding: 2, pt: 1.5 }}>
									<Button
										fullWidth
										variant="contained"
										startIcon={<AddIcon />}
										onClick={() => handleImportPrebuiltGuide(guide.id)}
										sx={{
											backgroundColor: "rgb(47, 127, 224)",
												color: "#fff",
											textTransform: "none",
											fontWeight: 600,
											borderRadius: 2,
												py: 1.25,
												boxShadow: "0 4px 12px rgba(47, 127, 224,0.3)",
												transition: "all 0.2s ease",
											"&:hover": {
													backgroundColor: "rgb(37, 117, 214)",
													boxShadow: "0 6px 16px rgba(47, 127, 224,0.4)",
													transform: "translateY(-1px)",
											},
										}}
									>
										Añadir a mis guías
									</Button>
								</CardActions>
							</Card>
						</Grid>
						);
					})}
					{prebuiltGuides.length === 0 && (
						<Grid item xs={12}>
							<Box
								sx={{
									textAlign: "center",
									p: 6,
									border: "2px dashed rgba(148,163,184,0.3)",
									borderRadius: 3,
									backgroundColor: "rgba(148,163,184,0.02)",
								}}
							>
								<AutoStoriesIcon
									sx={{
										fontSize: 64,
										color: "text.secondary",
										mb: 2,
										opacity: 0.5,
									}}
								/>
								<Typography
									variant="h6"
									sx={{ color: "text.secondary", mb: 1, fontWeight: 600 }}
								>
									No hay guías precargadas
								</Typography>
								<Typography variant="body2" color="text.secondary">
									No hay guías precargadas disponibles en este momento.
								</Typography>
							</Box>
						</Grid>
					)}
				</Grid>
			)}
		</Box>
	);
};

export default ExternalGuides;
